import {
  EMBED_PROTOCOL,
  isEmbedMessage,
  isUuid,
  type EmbedMessage,
  type EmbedContext,
  type HostState,
  type EmbedProduct,
} from "./embed-protocol";

interface Options {
  readonly origin: string;
  readonly hostOrigin: string;
  readonly instanceId: string;
  readonly userId: string;
  readonly frame: () => Window | null | undefined;
  readonly state: () => HostState;
  readonly context: () => EmbedContext | null;
  readonly onAvailable: (available: boolean) => void;
  readonly onDocument: () => void;
  readonly minimize: () => void;
  readonly activity: (sending: boolean, unread: boolean) => void;
  readonly contentSize: (height: number) => void;
}

/** 子が bootstrap を終えるまでの初期期限（ms）。 */
const INITIAL_TIMEOUT_MS = 15000;
/**
 * 再検証（`verify`。表示直前と一時障害からの回復時）に許す待ち時間（ms）。
 * タイマーと実時刻の両方で判定する。
 */
const VERIFY_TIMEOUT_MS = 10000;
/** `ready` で更新される利用可否の有効期限（ms）。子は 45 秒ごとに再検証する契約。 */
const LEASE_TIMEOUT_MS = 75000;
/** 退役させた文書 nonce の保持上限。無制限に増えないよう打ち切る。 */
const RETIRED_NONCE_MAX = 100;
/** `navigate` の重複判定に使う requestId の保持上限。 */
const NAVIGATION_ID_MAX = 100;

/**
 * Connect Feedback の埋め込み iframe と対話するホスト側の状態機械。
 *
 * 子を信頼しないため、`origin` / `source` / `instanceId` / `documentNonce` / 世代の
 * すべてが一致したメッセージだけを処理する。本プロジェクトは親画面の遷移要求
 * （`navigate`）を受け付けないため、契約どおり `ok: false` で応答するだけにする。
 */
export function createFeedbackHostBridge(options: Options) {
  let disposed = false;
  let readyAllowed = true;
  let nonce: string | undefined;
  let product: EmbedProduct | undefined;
  const retired = new Set<string>();
  const navigationIds = new Set<string>();
  let lease: ReturnType<typeof setTimeout> | undefined;
  let initialUntil = Date.now() + INITIAL_TIMEOUT_MS;
  let initial: ReturnType<typeof setTimeout> | undefined = setTimeout(fail, INITIAL_TIMEOUT_MS);
  let pending:
    | {
        readonly id: string;
        readonly generation: number;
        /** 再検証の期限（`Date.now()` 基準）。停止・復帰でタイマー通知が遅れても実時刻で失効させる。 */
        readonly until: number;
        readonly resolve: (ok: boolean) => void;
        readonly timer: ReturnType<typeof setTimeout>;
      }
    | undefined;

  function send(
    message: Omit<EmbedMessage, "protocol" | "version" | "instanceId" | "documentNonce">
  ): void {
    if (disposed || !nonce) return;
    options.frame()?.postMessage(
      {
        protocol: EMBED_PROTOCOL,
        version: 1,
        instanceId: options.instanceId,
        documentNonce: nonce,
        ...message,
      },
      options.origin
    );
  }

  function finish(ok: boolean): void {
    const p = pending;
    pending = undefined;
    if (p) {
      clearTimeout(p.timer);
      p.resolve(ok);
    }
  }

  /** 利用不可へ落とす。保留中の検証も解決し、FAB を消させる。 */
  function fail(): void {
    readyAllowed = false;
    clearTimeout(initial);
    clearTimeout(lease);
    initial = undefined;
    finish(false);
    options.onAvailable(false);
  }

  function receive(event: MessageEvent<unknown>): void {
    const value = event.data;
    if (
      disposed ||
      event.origin !== options.origin ||
      event.source !== options.frame() ||
      !isEmbedMessage(value) ||
      value.instanceId !== options.instanceId
    ) {
      return;
    }
    if (value.type === "hello") {
      if (retired.has(value.documentNonce)) return;
      if (nonce !== value.documentNonce) {
        if (retired.size >= RETIRED_NONCE_MAX) {
          fail();
          return;
        }
        if (nonce) {
          retired.add(nonce);
          fail();
          initialUntil = Date.now() + INITIAL_TIMEOUT_MS;
          initial = setTimeout(fail, INITIAL_TIMEOUT_MS);
          readyAllowed = true;
        }
        nonce = value.documentNonce;
        product = undefined;
        navigationIds.clear();
        options.onDocument();
      }
      send({
        type: "init",
        payload: {
          ...options.state(),
          visible: false,
          hostOrigin: options.hostOrigin,
          userId: options.userId,
        },
      });
      return;
    }
    if (value.documentNonce !== nonce) return;
    if (value.type === "ready") {
      // タイマー通知が遅れても実時刻で初期期限を判定する（スリープ復帰などで遅延する）。
      if (initial !== undefined && Date.now() >= initialUntil) {
        fail();
        return;
      }
      const p = value.payload;
      if (p.userId !== options.userId || (product && product.productId !== p.product.productId)) {
        fail();
        return;
      }
      const expected = pending;
      if (!expected && !readyAllowed) return;
      if (
        expected
          ? value.requestId !== expected.id || p.openGeneration !== expected.generation
          : value.requestId !== undefined || p.openGeneration !== options.state().openGeneration
      ) {
        return;
      }
      // 再検証への応答は実時刻で期限を判定する（期限ちょうども失効）。タイマー通知が遅れても
      // 期限後の応答で利用可能へ戻さず、期限タイマーと同じ `fail` で一度だけ終える。
      if (expected && Date.now() >= expected.until) {
        fail();
        return;
      }
      readyAllowed = true;
      product = p.product;
      clearTimeout(initial);
      clearTimeout(lease);
      initial = undefined;
      lease = setTimeout(fail, LEASE_TIMEOUT_MS);
      options.onAvailable(true);
      finish(true);
      return;
    }
    if (value.type === "unavailable") {
      if (
        pending
          ? value.requestId === pending.id
          : value.payload.openGeneration === options.state().openGeneration
      ) {
        fail();
      }
      return;
    }
    if (value.type === "activity") {
      options.activity(value.payload.sending, value.payload.unread);
      return;
    }
    // ここから下は「表示中かつ現在世代」に限って処理する（非表示中の窓へ波及させない）。
    const state = options.state();
    if (
      !readyAllowed ||
      !state.visible ||
      !("openGeneration" in value.payload) ||
      value.payload.openGeneration !== state.openGeneration
    ) {
      return;
    }
    if (value.type === "minimize") {
      options.minimize();
      return;
    }
    if (value.type === "content-size") {
      options.contentSize(value.payload.height);
      return;
    }
    if (value.type === "get-context") {
      send({
        type: "context-result",
        requestId: value.requestId,
        payload: {
          openGeneration: state.openGeneration,
          context: options.context(),
        },
      });
      return;
    }
    if (value.type === "navigate" && isUuid(value.requestId)) {
      // 本プロジェクトは子からの親画面遷移を提供しないため、常に拒否で応答する。
      // 同じ requestId の再送へ二重に応答しないよう、処理済み ID を記録する。
      const requestId = value.requestId;
      if (navigationIds.has(requestId) || navigationIds.size >= NAVIGATION_ID_MAX) return;
      navigationIds.add(requestId);
      send({
        type: "navigate-result",
        requestId,
        payload: { openGeneration: state.openGeneration, ok: false },
      });
    }
  }

  window.addEventListener("message", receive);

  return {
    /** 初回接続に一度も成功していない文書か（作り直しの判定に使う）。 */
    needsRestart: (): boolean => !disposed && !product && !readyAllowed,
    acceptsDocument: (value: string): boolean => !disposed && nonce === value,
    update(): void {
      send({ type: "host-state", payload: options.state() });
    },
    verify(generation: number): Promise<boolean> {
      finish(false);
      if (!nonce || disposed) return Promise.resolve(false);
      return new Promise((resolve) => {
        const id = crypto.randomUUID();
        pending = {
          id,
          generation,
          until: Date.now() + VERIFY_TIMEOUT_MS,
          resolve,
          timer: setTimeout(fail, VERIFY_TIMEOUT_MS),
        };
        send({
          type: "verify",
          requestId: id,
          payload: { openGeneration: generation },
        });
      });
    },
    focus(): void {
      if (options.state().visible) {
        send({
          type: "focus",
          payload: { openGeneration: options.state().openGeneration },
        });
      }
    },
    dispose(): void {
      disposed = true;
      clearTimeout(initial);
      clearTimeout(lease);
      finish(false);
      options.onDocument();
      window.removeEventListener("message", receive);
    },
  };
}
